import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CreditCardIcon, TruckIcon, UserIcon } from 'lucide-react';
const Checkout = () => {
  const navigate = useNavigate();
  const {
    user,
    isLoggedIn
  } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutDetails, setCheckoutDetails] = useState({
    deliveryAddress: '',
    contactNumber: '',
    paymentMethod: 'Cash on Delivery',
    specialInstructions: ''
  });
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    addresses: []
  });
  useEffect(() => {
    // Check if user is logged in
    if (!isLoggedIn || !user) {
      navigate('/login');
      return;
    }
    // Load cart from localStorage with user-specific key
    const savedCart = localStorage.getItem(`cart_${user.email}`);
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      if (parsedCart.length === 0) {
        navigate('/customer'); // Redirect if cart is empty
        return;
      }
      setCart(parsedCart);
    } else {
      navigate('/customer'); // Redirect if cart is empty
      return;
    }
    // Load profile from localStorage
    const savedProfile = localStorage.getItem(`profile_${user.email}`);
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile);
      setProfile(parsedProfile);
      // Pre-fill checkout details from profile
      setCheckoutDetails(prev => ({
        ...prev,
        contactNumber: parsedProfile.contactNumber || '',
        deliveryAddress: parsedProfile.addresses && parsedProfile.addresses.length > 0 ? parsedProfile.addresses.find(addr => addr.isDefault)?.address || parsedProfile.addresses[0].address : ''
      }));
    }
    // Load any saved checkout details
    const savedCheckout = localStorage.getItem(`checkoutDetails_${user.email}`);
    if (savedCheckout) {
      setCheckoutDetails(JSON.parse(savedCheckout));
    }
    setLoading(false);
  }, [user, isLoggedIn, navigate]);
  // Save checkout details to localStorage whenever they change
  useEffect(() => {
    if (isLoggedIn && user && checkoutDetails) {
      localStorage.setItem(`checkoutDetails_${user.email}`, JSON.stringify(checkoutDetails));
    }
  }, [checkoutDetails, user, isLoggedIn]);
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setCheckoutDetails({
      ...checkoutDetails,
      [name]: value
    });
  };
  const handleAddressSelect = address => {
    setCheckoutDetails({
      ...checkoutDetails,
      deliveryAddress: address
    });
  };
  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };
  const deliveryFee = 20.0;
  const calculateTotal = () => {
    return calculateSubtotal() + deliveryFee;
  };
  const validateForm = () => {
    if (!checkoutDetails.deliveryAddress.trim()) {
      alert('Please enter a delivery address');
      return false;
    }
    if (!checkoutDetails.contactNumber.trim()) {
      alert('Please enter a contact number');
      return false;
    }
    return true;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    // Save checkout details for payment page
    const orderDetails = {
      subtotal: calculateSubtotal(),
      deliveryFee: deliveryFee,
      total: calculateTotal(),
      ...checkoutDetails
    };
    localStorage.setItem(`checkoutDetails_${user.email}`, JSON.stringify(orderDetails));
    // Redirect to payment page
    navigate('/payment');
  };
  if (loading) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-732">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800" data-id="element-733"></div>
      </div>;
  }
  if (cart.length === 0) {
    return <div className="bg-amber-50 min-h-screen py-8 flex justify-center items-center" data-id="element-734">
        <div className="max-w-md mx-auto px-4 text-center" data-id="element-735">
          <h2 className="text-2xl font-bold text-amber-900 mb-4" data-id="element-736">
            Your Cart is Empty
          </h2>
          <p className="text-amber-800 mb-6" data-id="element-737">
            Add some items to your cart before checking out.
          </p>
          <button onClick={() => navigate('/customer')} className="px-4 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900" data-id="element-738">
            Return to Menu
          </button>
        </div>
      </div>;
  }
  return <div className="bg-amber-50 min-h-screen py-8" data-id="element-739">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-740">
        <h1 className="text-2xl font-bold text-amber-900 mb-6" data-id="element-741">Checkout</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-742">
          {/* Checkout Form */}
          <div className="lg:col-span-2" data-id="element-743">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-744">
              {/* Delivery Information */}
              <div className="p-6 border-b border-gray-200" data-id="element-745">
                <div className="flex items-center mb-4" data-id="element-746">
                  <TruckIcon className="h-5 w-5 text-amber-800 mr-2" data-id="element-747" />
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-748">
                    Delivery Information
                  </h2>
                </div>
                <div className="space-y-4" data-id="element-749">
                  <div data-id="element-750">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-751">
                      Delivery Address
                    </label>
                    <textarea name="deliveryAddress" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500" value={checkoutDetails.deliveryAddress} onChange={handleInputChange} required data-id="element-752" />
                  </div>
                  {profile.addresses && profile.addresses.length > 0 && <div data-id="element-753">
                      <p className="text-sm text-gray-600 mb-2" data-id="element-754">
                        Select from saved addresses:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-755">
                        {profile.addresses.map(addr => <button key={addr.id} type="button" onClick={() => handleAddressSelect(addr.address)} className="text-left px-3 py-2 border border-gray-300 rounded-md hover:bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-500" data-id="element-756">
                            <span className="block font-medium text-sm" data-id="element-757">
                              {addr.name}
                            </span>
                            <span className="block text-xs text-gray-600 mt-1" data-id="element-758">
                              {addr.address}
                            </span>
                          </button>)}
                      </div>
                    </div>}
                  <div data-id="element-759">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-760">
                      Contact Number
                    </label>
                    <input type="tel" name="contactNumber" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500" value={checkoutDetails.contactNumber} onChange={handleInputChange} placeholder="+63 912 345 6789" required data-id="element-761" />
                  </div>
                  <div data-id="element-762">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-763">
                      Special Instructions (Optional)
                    </label>
                    <textarea name="specialInstructions" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500" value={checkoutDetails.specialInstructions} onChange={handleInputChange} placeholder="E.g., Landmark, gate code, delivery preferences" data-id="element-764" />
                  </div>
                </div>
              </div>
              {/* Payment Method */}
              <div className="p-6" data-id="element-765">
                <div className="flex items-center mb-4" data-id="element-766">
                  <CreditCardIcon className="h-5 w-5 text-amber-800 mr-2" data-id="element-767" />
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-768">
                    Payment Method
                  </h2>
                </div>
                <div className="space-y-3" data-id="element-769">
                  <label className="flex items-center" data-id="element-770">
                    <input type="radio" name="paymentMethod" value="Cash on Delivery" checked={checkoutDetails.paymentMethod === 'Cash on Delivery'} onChange={handleInputChange} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300" data-id="element-771" />
                    <span className="ml-2 text-gray-700" data-id="element-772">Cash on Delivery</span>
                  </label>
                  <label className="flex items-center" data-id="element-773">
                    <input type="radio" name="paymentMethod" value="GCash" checked={checkoutDetails.paymentMethod === 'GCash'} onChange={handleInputChange} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300" data-id="element-774" />
                    <span className="ml-2 text-gray-700" data-id="element-775">GCash</span>
                  </label>
                  <label className="flex items-center" data-id="element-776">
                    <input type="radio" name="paymentMethod" value="Credit/Debit Card" checked={checkoutDetails.paymentMethod === 'Credit/Debit Card'} onChange={handleInputChange} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300" data-id="element-777" />
                    <span className="ml-2 text-gray-700" data-id="element-778">
                      Credit/Debit Card
                    </span>
                  </label>
                </div>
                {checkoutDetails.paymentMethod === 'GCash' && <div className="mt-4 p-4 bg-blue-50 rounded-md" data-id="element-779">
                    <p className="text-sm text-blue-800" data-id="element-780">
                      Please pay to GCash number: <strong data-id="element-781">0912 345 6789</strong>{' '}
                      upon order confirmation. Our delivery personnel will
                      verify your payment.
                    </p>
                  </div>}
                {checkoutDetails.paymentMethod === 'Credit/Debit Card' && <div className="mt-4 p-4 bg-amber-50 rounded-md" data-id="element-782">
                    <p className="text-sm text-amber-800" data-id="element-783">
                      You'll be asked to present your card for payment upon
                      delivery.
                    </p>
                  </div>}
              </div>
            </form>
          </div>
          {/* Order Summary */}
          <div className="lg:col-span-1" data-id="element-784">
            <div className="bg-white rounded-lg shadow-md overflow-hidden sticky top-4" data-id="element-785">
              <div className="p-6 border-b border-gray-200" data-id="element-786">
                <div className="flex items-center mb-4" data-id="element-787">
                  <UserIcon className="h-5 w-5 text-amber-800 mr-2" data-id="element-788" />
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-789">
                    Order Summary
                  </h2>
                </div>
                <div className="mt-6 space-y-4" data-id="element-790">
                  {cart.map(item => <div key={item.id} className="flex justify-between" data-id="element-791">
                      <div data-id="element-792">
                        <span className="text-gray-900" data-id="element-793">
                          {item.quantity} x{' '}
                        </span>
                        <span className="text-gray-900" data-id="element-794">{item.name}</span>
                      </div>
                      <span className="text-gray-900" data-id="element-795">
                        ₱{(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>)}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-796">
                  <div className="flex justify-between" data-id="element-797">
                    <span className="text-gray-600" data-id="element-798">Subtotal</span>
                    <span className="text-gray-900" data-id="element-799">
                      ₱{calculateSubtotal().toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-2" data-id="element-800">
                    <span className="text-gray-600" data-id="element-801">Delivery Fee</span>
                    <span className="text-gray-900" data-id="element-802">
                      ₱{deliveryFee.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-4 pt-4 border-t border-gray-200" data-id="element-803">
                    <span className="text-lg font-medium text-gray-900" data-id="element-804">
                      Total
                    </span>
                    <span className="text-lg font-medium text-gray-900" data-id="element-805">
                      ₱{calculateTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-806">
                <button type="submit" onClick={handleSubmit} className="w-full py-3 px-4 bg-amber-800 text-white rounded-md hover:bg-amber-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" data-id="element-807">
                  Proceed to Payment
                </button>
                <Link to="/customer" className="w-full mt-3 py-3 px-4 bg-white border border-amber-800 text-amber-800 rounded-md hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 flex items-center justify-center" data-id="element-808">
                  Back to Menu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Checkout;